import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";

import classNames from "classnames";

import { useRootAppStore } from "@hooks";
import { checkIfMessageIsLink } from "@utils/chat";
import { Button } from "@components/buttons";
import CustomMessagePreview from "./CustomMessagePreview";
import ChatReplyMessagePreview from "./ChatReplyMessagePreview";
import useReplyMessage from "../shared/useReplyMessage";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { SupportChatInfoBox } from "../shared";

export default observer(function SendMessage() {
	const { chatViewStore } = useRootAppStore();
	const [inputValue, setInputValue] = useState<string>("");
	const { t } = useTranslation();

	const shouldDisableSubmit =
		chatViewStore.messageSendingLoader ||
		((!inputValue || inputValue.trim().length == 0) &&
			!chatViewStore.sharedBetSlipData);

	const isLink = checkIfMessageIsLink(inputValue);

	const replyingMessage = useReplyMessage(inputValue, isLink);

	const sendMsg = async (
		e:
			| React.FormEvent<HTMLFormElement>
			| React.KeyboardEvent<HTMLTextAreaElement>
	) => {
		e.preventDefault();

		if (!chatViewStore.selectedMessageToReplyOn) {
			chatViewStore.chatChannelHandler.sendMessage(
				inputValue,
				isLink,
				chatViewStore.sharedBetSlipData?.hasBetBuilder
			);
		} else if (replyingMessage) {
			chatViewStore.chatChannelHandler.replyToMessage(replyingMessage);
		}
		setInputValue("");
	};

	const handleTextAreaSubmit = (
		e: React.KeyboardEvent<HTMLTextAreaElement>
	) => {
		if (e.key === "Enter" && e.shiftKey == false && !shouldDisableSubmit) {
			e.preventDefault();
			if (inputValue || chatViewStore.sharedBetSlipData) {
				sendMsg(e);
			}
		}
	};

	return (
		<ErrorBoundaryWithPropagation componentName={"Desktop/SendMessage.tsx"}>
			<form
				className={classNames("chat__footer", {
					"chat__footer--scrolling":
						chatViewStore.replyScrollingLoader,
				})}
				onSubmit={sendMsg}
				aria-disabled={chatViewStore.replyScrollingLoader}
			>
				<SupportChatInfoBox store={chatViewStore} />
				<ChatReplyMessagePreview />

				<CustomMessagePreview
					isMultiOffer={
						chatViewStore.sharedBetSlipData?.hasBetBuilder
					}
				/>

				<div
					className="chat__textbox"
					aria-disabled={chatViewStore.replyScrollingLoader}
					style={{
						pointerEvents: chatViewStore.replyScrollingLoader
							? "none"
							: "auto",
					}}
				>
					<textarea
						maxLength={10000}
						id="chat-textarea"
						onKeyDown={handleTextAreaSubmit}
						rows={3}
						value={inputValue}
						className="chat__textarea input input--med input--textarea"
						placeholder={t("CHAT.ENTER_MESSAGE")}
						onChange={(e) => setInputValue(e.target.value)}
						autoFocus
					></textarea>

					<Button
						className="btn--square btn--square--sml btn--accent btn--icon chat__send"
						iconClassName="u-icon--sml u-icon--arrow--send u-color--accent--text"
						iconOnly
						disabled={shouldDisableSubmit}
					/>
				</div>
			</form>
		</ErrorBoundaryWithPropagation>
	);
});
